import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type {
  CustomColumnDefinition,
  CustomFieldDefinition,
} from "@/app/api/types/featureFlags";
import {
  findMissingRequiredCustomFields,
  findZeroPriceIssues,
  findZeroQuantityIssues,
  type LineFieldIssue,
  type ZeroQuantityField,
} from "./validationService";

/**
 * Centralized pre-confirm warning pipeline.
 *
 * A single place that runs every flow-level check gating a Confirm/Export and
 * returns ONE aggregated, structured list. Every warning carries WHICH line and
 * WHICH field so the dialog can always name them. Add a new confirm-time check
 * by extending `collectConfirmWarnings` — callsites and the dialog need no
 * change.
 *
 * (The create-time duplicate warning and the delivery-date warning are separate
 * lifecycle events with their own UX and are intentionally not folded in here.)
 */

export type WarningCategory = "required" | "zeroQuantity" | "zeroPrice";

export interface ConfirmWarning extends LineFieldIssue {
  category: WarningCategory;
  /** true → `fieldLabel` is a custom-field label (render verbatim); false → a standard field key to translate. */
  custom: boolean;
}

export interface CollectWarningsInput {
  /** All pending flows — checked for zero quantity + required custom fields. */
  flows: PreOrderFlow[];
  /** Purchase-category pending flows — checked for zero price (defaults to `flows`). */
  priceFlows?: PreOrderFlow[];
  /** Run the purchase-only zero-price check. */
  isPurchase: boolean;
  /** Standard fields to zero-check (feature-flag derived: quantity always; unit/weight when shown). */
  zeroFields: ReadonlyArray<ZeroQuantityField>;
  customFieldDefs: ReadonlyArray<CustomFieldDefinition>;
  customColumnDefs: ReadonlyArray<CustomColumnDefinition>;
}

export function collectConfirmWarnings(
  input: CollectWarningsInput,
): ConfirmWarning[] {
  const {
    flows,
    priceFlows,
    isPurchase,
    zeroFields,
    customFieldDefs,
    customColumnDefs,
  } = input;
  const out: ConfirmWarning[] = [];

  const tag = (
    category: WarningCategory,
    custom: boolean,
    issues: LineFieldIssue[],
  ) => {
    for (const issue of issues) out.push({ ...issue, category, custom });
  };

  tag(
    "required",
    true,
    findMissingRequiredCustomFields(flows, customFieldDefs, customColumnDefs),
  );
  tag("zeroQuantity", false, findZeroQuantityIssues(flows, zeroFields));
  if (isPurchase) {
    tag("zeroPrice", false, findZeroPriceIssues(priceFlows ?? flows));
  }

  return out;
}
